import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { DecimalConfig } from '../configs/decimal.config';

@Directive({
  selector: '[lumDecimalLimit]',
})
export class DecimalLimitDirective implements OnInit {
  @Input() decimalPlaces :number = DecimalConfig.defaultPlaces;
  @Input() allowNegative :boolean = true;
  @Input() invalidKeys :string[] = [];
  @Input() lumDecimalLimit :boolean = true;

  private regex :RegExp = new RegExp(/^-?[0-9]*(\.[0-9]*){0,1}$/g);

  constructor(
    private elementRef :ElementRef,
    @Optional() private ngControl :NgControl,
  ) {}

  ngOnInit() :void {
    this.elementRef.nativeElement.type = 'text';

    setTimeout(() => {
      const initialValue = this.elementRef.nativeElement.value;
      if (initialValue) {
        this.elementRef.nativeElement.value = this.formatValue(initialValue);
      }
    });
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event :KeyboardEvent) {
    if (this.lumDecimalLimit === false) return;

    const inputValue = this.elementRef.nativeElement.value;
    const caretPosition = this.elementRef.nativeElement.selectionStart;
    const decimalPart = (inputValue.split('.')[1] || '').length;

    const decimalIndex = inputValue.indexOf('.');
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Decimal',
      'Tab',
      '.',
    ];
    let invalidKeys = ['e', 'E', '+'];

    // Allow Ctrl + V/v and Ctrl + C/c for paste and copy
    if (
      (event.ctrlKey || event.metaKey) &&
      ['v', 'V', 'c', 'C'].includes(event.key)
    ) {
      return;
    }

    // if input type is text, prevent from inputting non numerical values
    if (!allowedKeys.includes(event.key) && !String(event.key).match(this.regex)) {
      event.preventDefault();

      return;
    }

    // if set to no decimal places, dont allow dot (.) key
    if (
      this.decimalPlaces === 0 ||
      (event.key === '.' && inputValue.includes('.'))
    ) {
      event.preventDefault();

      return;
    }

    // Allow backspace, delete, left arrow, right arrow, and decimal point
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // if negative value is allowed and '-' key is pressed
    if (this.allowNegative && event.key === '-') {
      // if '-' key pressed and caret is not at 0 position, prevent action
      if (caretPosition !== 0) {
        event.preventDefault();

        return;
      }

      // block '-' input when already exist in the first character
      if (inputValue[0] === '-' && caretPosition === 0) {
        event.preventDefault();

        return;
      }
    } else if (!this.allowNegative && event.key === '-') {
      // Prevent '-' key if negative values are not allowed
      event.preventDefault();

      return;
    }

    // merged invalid keys from input if present
    if (invalidKeys.length) {
      invalidKeys = [...new Set(invalidKeys.concat(this.invalidKeys))];
    }

    // Prevent if keypress is a non-decimal character, after the decimal point, and exceeding the decimal limit
    if (
      decimalIndex !== -1 &&
      event.key !== '.' &&
      decimalPart >= this.decimalPlaces &&
      caretPosition > decimalIndex
    ) {
      setTimeout(() => {
        this.elementRef.nativeElement.value = this.formatValue(
          this.elementRef.nativeElement.value,
        );
      }, 0);
      event.preventDefault();

      return;
    }
  }

  @HostListener('blur')
  onBlur() {
    this.elementRef.nativeElement.value = this.formatValue(
      this.elementRef.nativeElement.value,
    );
  }

  @HostListener('paste', ['$event'])
  onPaste(event :ClipboardEvent) {
    if (this.lumDecimalLimit === false) return;

    event.preventDefault();

    const clipboardData = event.clipboardData || window['clipboardData'];
    let pastedText = clipboardData.getData('text');

    // Remove commas from the pasted text
    pastedText = pastedText.replace(/,/g, '');

    const value = this.formatValue(pastedText);

    // Update the Angular form control value or the element value (whichever is
    // more appropriate).
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(value);
    } else {
      this.elementRef.nativeElement.value = value;
    }
  }

  private formatValue(value :string) :string {
    if (value.indexOf('.') !== -1) {
      const parts = value.split('.');
      // Remove trailing zeros and ensure the minimum number of decimal places as set in the directive
      const decimalPart = parts[1].replace(/0+$/, '');
      if (decimalPart === '') {
        return parts[0]; // If there's nothing after removing zeros, return just the integer part
      } else {
        return `${parts[0]}.${decimalPart}`; // Return the formatted number
      }
    }

    return value;
  }
}
